import React from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import { useAppDispatch, useAppSelector } from "@/redux/store";
import { addCode } from "@/redux/profile/settings/changePass/slice";

import styles from "./SmsRegCodeForm.module.scss";

import { ValidMessages } from "@/resources/locale";
import { SubmitValueType } from "./types";
import { fetchReg, fetchRequestCode, FetchBitrixRegistrationUser, fetchUpdateUser } from "@/redux/profile/authAndReg/asyncActions";
import { AuthCodeInput as AuthCode } from "@/components/Inputs/AuthCodeInput";
import {useInterval} from "@/hooks/useInterval";
import { phoneMaskFormat } from "@/utils/form/phoneMask";
import { setTokenToStorage } from "@/utils";
import { getSendCodeErrorMessage } from "@/utils/sendCodeError";

const SMS_COOLDOWN_KEY = 'sms_last_sent';
const SMS_COOLDOWN_MS = 60000;

function getSmsCooldownRemaining(): number {
	const lastSent = sessionStorage.getItem(SMS_COOLDOWN_KEY);
	if (!lastSent) return 0;
	const elapsed = Date.now() - Number(lastSent);
	return elapsed < SMS_COOLDOWN_MS ? Math.ceil((SMS_COOLDOWN_MS - elapsed) / 1000) : 0;
}

type SmsRegCodeFormProps = {
	handleSubmitForm?: () => void
	handleErrorForm?: () => void
}
export const SmsRegCodeForm: React.FC<SmsRegCodeFormProps> = (props) => {
	const { handleSubmitForm, handleErrorForm } = props;
	const { phone, name, email, password } = useAppSelector(state => state.changePass);
	const dispatch = useAppDispatch();
	const [errorCode, setErrorCode] = React.useState(false)
	const [errorMessage, setErrorMessage] = React.useState<string>("")
	const [resendError, setResendError] = React.useState<string>("")
	const [timer, setTimer] = React.useState(() => {
		const remaining = getSmsCooldownRemaining();
		return remaining > 0 ? remaining : 60;
	})

	const initialValues = {
		code: [""],
	};

	const validationSchema = Yup.object({
		code: Yup.array().of(Yup.string().required("Required")),
	});

	const handleSubmit = async (values: SubmitValueType) => {
		try {
			const code = values.code.join("");

			const confirmData = {
				phone,
				code: Number(code),
			};

			const token = await dispatch(fetchRequestCode(confirmData)).unwrap();
			setTokenToStorage(token);
			
			const bitrixData = {
				name,
				phone,
				email,
			};
			
			const partnerId = await dispatch(FetchBitrixRegistrationUser(bitrixData)).unwrap();
			
			const updateUserData = {
				name,
				phone,
				email,
				partner_id: partnerId || '',
			};
			
			await dispatch(fetchUpdateUser(updateUserData)).unwrap();
	
			handleSubmitForm && handleSubmitForm();
			setErrorCode(false);
			setErrorMessage("");
		} catch (error: any) {
			const errorMsg = typeof error === 'string' 
				? error 
				: error?.message || 'Неверный код';
			
			setErrorCode(true);

			if (errorMsg.includes('истекло') || errorMsg.includes('истек')) {
				setErrorMessage("Время восстановления пароля истекло");
			} else if (errorMsg.includes('unique constraint') || errorMsg.includes('P2002')) {
				setErrorMessage("Пользователь с данным номером уже существует");
			} else if (errorMsg.includes('partner_id')) {
				setErrorMessage("Ошибка регистрации: не удалось получить partner_id");
			} else {
				setErrorMessage("Неверный код");
			}
			
			if (handleErrorForm) {
				handleErrorForm();
			}
		}
	};

	useInterval(() => {
		setTimer(timer - 1)
	}, timer > 0? 1000 : null)

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
			validateOnChange={false}
			validateOnBlur={false}
		>
			{(formik) => {
				return (
					<Form className={styles.form}>
						<p className={styles.description}>
							Вам был выслан код подтверждения <br/> на телефон {' '} {phoneMaskFormat(phone)}
						</p>

						<div className={styles.formSection}>
							<div className={styles.inputsWrapper}>
								<AuthCode
									containerClassName={styles.inputsWrapper}
									length={4}
									inputClassName={`${styles.input} ${errorCode ? styles.input_error : ''}`}
									allowedCharacters='numeric'
									onChange={(v)=>{
										formik.setValues({code:[v]})
										
										if (errorCode) {
											setErrorCode(false);
											setErrorMessage("");
										}

										if (v.length === 4) {
											formik.handleSubmit()
										}
									}}
								/>
							</div>
							{
								errorCode && errorMessage ? (
									<div className={`${styles.loadingMessage_error}`}>
										{errorMessage}
									</div>
								) : null
							}
							{resendError ? (
								<div className={`${styles.loadingMessage_error}`}>{resendError}</div>
							) : null}
						</div>

						<div className={styles.description}>
							{
								timer !== 0
									? (
										<span className={styles.timer}>
											Повторить через {' '}
											<span className={styles.timer_time}>
												{timer} секунд
											</span>
										</span>
									) :(
										<span
											className={styles.resend}
											onClick={async () => {
												try {
													const data = {
														name: name,
														email: email,
														phone: phone,
														password: password,
														partner_id: '1',
													};
										
													await dispatch(fetchReg(data)).unwrap();
													sessionStorage.setItem(SMS_COOLDOWN_KEY, String(Date.now()));
													setTimer(60);
													setErrorCode(false);
													setErrorMessage("");
													setResendError("");
													formik.setValues({code: [""]});
												} catch (error) {
													// unwrap() отдаёт rejectWithValue — строку с ответом бэка
													setResendError(getSendCodeErrorMessage(error));
												}
											}}
										>
											Отправить повторно
										</span>
									)
							}
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};
